<!-- My Media Accreditation Users -->
<template>
    <div>
        <div class="d-flex">
            <!--Back Button-->
            <app-btn @click.native="handleBack" class="mr-4" color="white" icon="arrowBack" icon-color="primary" />

            <!--Header-->
            <page-title
                icon="accreditation"
                :info="isOrganisationUser ? '' : 'Manage your Accreditation Users.'"
                :title="'My Accreditation' + (fullEventData?.eventData?.eventName ? ' - ' + fullEventData.eventData.eventName : '')"
            />
        </div>

        <v-divider class="mt-2" />

        <!--Getting Started Info-->
        <div
            v-if="!fullEventData?.accreditationEventData?.entityId"
            class="d-flex align-center justify-center"
            :style="$vuetify.breakpoint.width < 600 ? 'height: 50vh' : 'height: 70vh'"
        >
            <page-start-information title="Getting Started" :points="['Configure this event', 'Set it to Live']" />
        </div>

        <!--Passes Figures-->
        <div v-if="showAdminFeatures" class="mt-4" style="display: grid; grid-template-columns: repeat(5, 1fr); grid-gap: 16px">
            <!--Requested-->
            <div class="d-flex flex-column align-center cursorPointer black rounded-lg pa-4">
                <app-text color="white" size="large-bold">{{ numberOfPassesRequested }}</app-text>
                <app-text color="white" size="small">Requested</app-text>
            </div>

            <!--Approved-->
            <div class="d-flex flex-column align-center cursorPointer green rounded-lg pa-4">
                <app-text color="white" size="large-bold">{{ numberOfPassesApproved }}</app-text>
                <app-text color="white" size="small">Approved</app-text>
            </div>

            <!--Pending-->
            <div class="d-flex flex-column align-center cursorPointer orange rounded-lg pa-4">
                <app-text color="white" size="large-bold">{{ numberOfPassesPending }}</app-text>
                <app-text color="white" size="small">Pending</app-text>
            </div>

            <!--Rejected-->
            <div class="d-flex flex-column align-center cursorPointer red rounded-lg pa-4">
                <app-text color="white" size="large-bold">{{ numberOfPassesRejected }}</app-text>
                <app-text color="white" size="small">Rejected</app-text>
            </div>

            <!--Reserved-->
            <div class="d-flex flex-column align-center cursorPointer blue rounded-lg pa-4">
                <app-text color="white" size="large-bold">{{ numberOfPassesReserved }}</app-text>
                <app-text color="white" size="small">Reserved</app-text>
            </div>
        </div>

        <!--Action Bar-->
        <div class="d-flex align-center mt-4">
            <!--Search-->
            <app-form-field
                v-if="showAdminFeatures"
                form-type="textInput"
                append-icon="icons8-search"
                class="mr-4"
                :clearable="true"
                label="User Name"
                style="width: 100%"
                v-model.trim="searchByUserName"
            />

            <app-btn @click.native="handleUserSelection(getMIXCurrentUser())" icon="accreditation" label="Apply for Passes" />
        </div>

        <!--Clear Filters-->
        <div v-if="computedNumberOfActiveFilters > 0" class="d-flex justify-end mt-4">
            <app-btn @click.native="clearFilters" icon="close" label="Clear Filters" />
        </div>

        <template v-if="$vuetify.breakpoint.width >= 600 && fullEventData?.accreditationEventData?.entityId">
            <!-- Organization User View -->
            <template v-if="isOrganisationUser">
                <!--Your Pass-->
                <form-section-title class="mt-4" title="Your Pass" />
                <v-data-table class="appWhite rounded-lg mt-4" :headers="computedHeaders" :items="usersWhoHaveApplied">
                    <!--ID-->
                    <template v-slot:item.entityId="{ item }">
                        <app-text size="small">{{ item.entityId }}</app-text>
                    </template>

                    <!--Status-->
                    <template v-slot:item.accreditationPassStatus="{ item }">
                        <app-icon
                            v-if="item.passData.accreditationPassStatus === 'Approved'"
                            color="green"
                            icon="success"
                            size="32"
                            :title="item.passData.accreditationPassStatus"
                        />
                        <app-icon
                            v-if="item.passData.accreditationPassStatus === 'Pending'"
                            color="orange"
                            icon="pending"
                            size="32"
                            :title="item.passData.accreditationPassStatus"
                        />
                        <app-icon
                            v-if="item.passData.accreditationPassStatus === 'Rejected'"
                            color="red"
                            icon="cancel"
                            size="32"
                            :title="item.passData.accreditationPassStatus"
                        />
                        <app-icon
                            v-if="item.passData.accreditationPassStatus === 'Reserved'"
                            color="blue"
                            icon="reserved"
                            size="32"
                            :title="item.passData.accreditationPassStatus"
                        />
                    </template>

                    <!--UserName-->
                    <template v-slot:item.userName="{ item }">
                        <app-text size="small">{{ item.userData.userName }}</app-text>
                    </template>

                    <!--UserOrganisation-->
                    <template v-slot:item.userOrganisation="{ item }">
                        <app-text size="small">
                            {{
                                Array.isArray(item.passData?.accreditationPassOrganisationId)
                                    ? item.passData.accreditationPassOrganisationId.map((orgId) => getOrganisationName(orgId)).join(', ')
                                    : getOrganisationName(item.passData?.accreditationPassOrganisationId)
                            }}
                        </app-text>
                    </template>

                    <!--UserPosition-->
                    <template v-slot:item.userPosition="{ item }">
                        <app-text size="small">{{ item.userData.userPosition }}</app-text>
                    </template>

                    <!--UserPressType-->
                    <template v-slot:item.userPressType="{ item }">
                        <app-text size="small">{{ item.userData.userPressType || '-' }}</app-text>
                    </template>

                    <!--UserHolderType-->
                    <template v-slot:item.userHolderType="{ item }">
                        <app-text size="small">{{ item.passData?.accreditationPassHolderType || '-' }}</app-text>
                    </template>

                    <!--Action Button-->
                    <template v-slot:item.action="{ item }">
                        <app-icon @click.native="handleUserSelection(item)" class="cursorPointer" color="primary" icon="arrowForward" />
                    </template>
                </v-data-table>
            </template>

            <!-- Admin/Manager View -->
            <template v-else>
                <!--Users Who Have Applied-->
                <form-section-title class="mt-4" title="Users Who Have Applied" />
                <v-data-table class="appWhite rounded-lg mt-4" :headers="computedHeaders" :items="usersWhoHaveApplied">
                    <!--ID-->
                    <template v-slot:item.entityId="{ item }">
                        <app-text size="small">{{ item.entityId }}</app-text>
                    </template>

                    <!--Status-->
                    <template v-slot:item.accreditationPassStatus="{ item }">
                        <app-icon
                            v-if="item.passData.accreditationPassStatus === 'Approved'"
                            color="green"
                            icon="success"
                            size="32"
                            :title="item.passData.accreditationPassStatus"
                        />
                        <app-icon
                            v-if="item.passData.accreditationPassStatus === 'Pending'"
                            color="orange"
                            icon="pending"
                            size="32"
                            :title="item.passData.accreditationPassStatus"
                        />
                        <app-icon
                            v-if="item.passData.accreditationPassStatus === 'Rejected'"
                            color="red"
                            icon="cancel"
                            size="32"
                            :title="item.passData.accreditationPassStatus"
                        />
                        <app-icon
                            v-if="item.passData.accreditationPassStatus === 'Reserved'"
                            color="blue"
                            icon="reserved"
                            size="32"
                            :title="item.passData.accreditationPassStatus"
                        />
                    </template>

                    <!--UserName-->
                    <template v-slot:item.userName="{ item }">
                        <app-text size="small">{{ item.userData.userName }}</app-text>
                    </template>

                    <!--UserOrganisation-->
                    <template v-slot:item.userOrganisation="{ item }">
                        <app-text size="small">
                            {{
                                Array.isArray(item.passData?.accreditationPassOrganisationId)
                                    ? item.passData.accreditationPassOrganisationId.map((orgId) => getOrganisationName(orgId)).join(', ')
                                    : getOrganisationName(item.passData?.accreditationPassOrganisationId)
                            }}
                        </app-text>
                    </template>

                    <!--UserPosition-->
                    <template v-slot:item.userPosition="{ item }">
                        <app-text size="small">{{ item.userData.userPosition }}</app-text>
                    </template>

                    <!--UserPressType-->
                    <template v-slot:item.userPressType="{ item }">
                        <app-text size="small">{{ item.userData.userPressType || '-' }}</app-text>
                    </template>

                    <!--UserHolderType-->
                    <template v-slot:item.userHolderType="{ item }">
                        <app-text size="small">{{ item.passData?.accreditationPassHolderType || '-' }}</app-text>
                    </template>

                    <!--Action Button-->
                    <template v-slot:item.action="{ item }">
                        <app-icon @click.native="handleUserSelection(item)" class="cursorPointer" color="primary" icon="arrowForward" />
                    </template>
                </v-data-table>

                <!--Users Who Have Not Applied-->
                <div class="d-flex align-center justify-between my-8">
                    <app-btn
                        @click.native="showNotAppliedTable = !showNotAppliedTable"
                        :icon="showNotAppliedTable ? 'visibility_off' : 'visibility'"
                        :label="showNotAppliedTable ? 'Hide Users' : 'Show Users Who Have Not Applied'"
                        color="primary"
                    />
                </div>

                <form-section-title v-if="showNotAppliedTable" title="Users Who Have Not Applied" />
                <v-data-table
                    v-if="showNotAppliedTable"
                    class="appWhite rounded-lg mt-4"
                    :headers="computedHeadersForNotApplied"
                    :items="usersWhoHaveNotApplied"
                >
                    <!--ID-->
                    <template v-slot:item.entityId="{ item }">
                        <app-text size="small">{{ item.entityId }}</app-text>
                    </template>

                    <!--UserName-->
                    <template v-slot:item.userName="{ item }">
                        <app-text size="small">{{ item.userData.userName }}</app-text>
                    </template>

                    <!--UserOrganisation-->
                    <template v-slot:item.userOrganisation="{ item }">
                        <app-text size="small">
                            {{
                                Array.isArray(item.userData?.userOrganisation)
                                    ? item.userData.userOrganisation.map((orgId) => getOrganisationName(orgId)).join(', ')
                                    : getOrganisationName(item.userData?.userOrganisation)
                            }}
                        </app-text>
                    </template>

                    <!--UserPosition-->
                    <template v-slot:item.userPosition="{ item }">
                        <app-text size="small">{{ item.userData.userPosition }}</app-text>
                    </template>

                    <!--UserPressType-->
                    <template v-slot:item.userPressType="{ item }">
                        <app-text size="small">{{ item.userData.userPressType || '-' }}</app-text>
                    </template>

                    <!--Action Button-->
                    <template v-slot:item.action="{ item }">
                        <app-icon @click.native="handleUserSelection(item)" class="cursorPointer" color="primary" icon="arrowForward" />
                    </template>
                </v-data-table>
            </template>
        </template>

        <!--Mobile Cards-->
        <div v-if="$vuetify.breakpoint.width < 600">
            <!--No data message-->
            <app-text v-if="!computedPaginatedData.length" class="text-center mt-4" color="grey9">You have no Accreditation Events to view </app-text>

            <!--Cards-->
            <div
                v-for="item in computedPaginatedData"
                :key="item.passData.entityId"
                @click="handleUserSelection(item)"
                class="appWhite d-flex align-center rounded-lg mt-4 pa-4"
            >
                <!--Status Icons-->
                <div class="mr-4">
                    <app-icon
                        v-if="item.passData.accreditationPassStatus === 'Approved'"
                        color="green"
                        icon="success"
                        size="48"
                        :title="item.passData.accreditationPassStatus"
                    />
                    <app-icon
                        v-if="item.passData.accreditationPassStatus === 'Pending'"
                        color="orange"
                        icon="pending"
                        size="48"
                        :title="item.passData.accreditationPassStatus"
                    />
                    <app-icon
                        v-if="item.passData.accreditationPassStatus === 'Rejected'"
                        color="red"
                        icon="cancel"
                        size="48"
                        :title="item.passData.accreditationPassStatus"
                    />
                    <app-icon
                        v-if="item.passData.accreditationPassStatus === 'Reserved'"
                        color="blue"
                        icon="reserved"
                        size="48"
                        :title="item.passData.accreditationPassStatus"
                    />
                </div>

                <!--User Details-->
                <div style="width: 100%">
                    <app-text color="primary" size="normal-bold">{{ item.userData.userName }}</app-text>
                    <app-text color="grey9" size="small">{{ item.userData.userPosition }}</app-text>
                    <app-text size="small">
                        {{
                            Array.isArray(item.passData?.accreditationPassOrganisationId)
                                ? item.passData.accreditationPassOrganisationId.map((orgId) => getOrganisationName(orgId)).join(', ')
                                : getOrganisationName(item.passData?.accreditationPassOrganisationId)
                        }}
                    </app-text>

                    <v-divider class="my-2" />

                    <app-text color="grey9" size="small">{{ item.userData.userOutletPublication }}</app-text>
                    <div class="d-flex">
                        <app-text color="grey9" size="small">{{ item.userData.userPressType }}</app-text>
                        <app-text class="mx-2" color="greyD" size="small">|</app-text>
                        <app-text color="grey9" size="small">{{ item.passData?.accreditationPassHolderType || '-' }}</app-text>
                    </div>
                </div>
            </div>

            <!--Pagination Controls-->
            <div class="d-flex align-center justify-center mt-4">
                <app-btn @click.native="prevPage" :disabled="currentPage === 1" label="Previous" />
                <app-text class="mx-2">{{ currentPage }} / {{ computedTotalPages }}</app-text>
                <app-btn @click.native="nextPage" :disabled="currentPage === computedTotalPages" label="Next" />
            </div>
        </div>

        <!--Filters Panel ------------------------------------------------------------------------------------------ -->
        <filter-panel :is-filters-panel-visible="isFiltersPanelVisible" @toggle="toggleFiltersVisibility">
            <div class="pa-4">
                <!--Statuses-->
                <page-break-title title="Statuses" />
                <div class="mt-4" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); grid-gap: 16px">
                    <app-btn
                        @click.native="handleFilterStatusSelection('Pending')"
                        :block="true"
                        color="orange"
                        label="Pending"
                        :outlined="!filterByStatuses.includes('Pending')"
                    />
                    <app-btn
                        @click.native="handleFilterStatusSelection('Approved')"
                        :block="true"
                        color="green"
                        label="Approved"
                        :outlined="!filterByStatuses.includes('Approved')"
                    />
                    <app-btn
                        @click.native="handleFilterStatusSelection('Rejected')"
                        :block="true"
                        color="red"
                        label="Rejected"
                        :outlined="!filterByStatuses.includes('Rejected')"
                    />
                    <app-btn
                        @click.native="handleFilterStatusSelection('Reserved')"
                        :block="true"
                        color="blue"
                        label="Reserved"
                        :outlined="!filterByStatuses.includes('Reserved')"
                    />
                    <app-btn
                        @click.native="handleFilterStatusSelection('Not Applied')"
                        :block="true"
                        color="grey"
                        label="Not Applied"
                        :outlined="!filterByStatuses.includes('Not Applied')"
                    />
                </div>

                <!--Is Printed-->
                <page-break-title class="mt-8" title="Print Status" />
                <div class="mt-4">
                    <app-form-field
                        form-type="autoComplete"
                        :deletable-chips="true"
                        :items="['Printed', 'Not Printed']"
                        label="Print Status"
                        :multiple="true"
                        :small-chips="true"
                        v-model="filterByPrintStatus"
                    />
                </div>

                <!--Position-->
                <page-break-title class="mt-8" title="Position" />
                <div class="mt-4">
                    <app-form-field form-type="textInput" label="Position" v-model.trim="filterByPosition" />
                </div>

                <!--Media Types-->
                <page-break-title class="mt-8" title="Media Types" />
                <div class="mt-4">
                    <app-form-field
                        form-type="autoComplete"
                        :deletable-chips="true"
                        :items="['Broadcast', 'Media']"
                        label="Media Types"
                        :multiple="true"
                        :small-chips="true"
                        v-model="filterByMediaTypes"
                    />
                </div>

                <!--Holder Types-->
                <page-break-title class="mt-8" title="Rights Holder Types" />
                <div class="mt-4">
                    <app-form-field
                        form-type="autoComplete"
                        :deletable-chips="true"
                        :items="['Rights Holder', 'Non-Rights Holder']"
                        label="Rights Holder Types"
                        :multiple="true"
                        :small-chips="true"
                        v-model="filterByHolderTypes"
                    />
                </div>
            </div>
        </filter-panel>
    </div>
</template>

<script>
export default {
    name: 'MyMediaAccreditationUsers',

    components: {},

    props: {
        fullEventData: {
            type: Object,
            required: true
        },
        showAdminFeatures: {
            type: Boolean,
            default: true
        }
    },

    data: () => ({
        isFiltersPanelVisible: false,
        filterByHolderTypes: [],
        filterByMediaTypes: [],
        filterByPosition: '',
        filterByPrintStatus: [],
        filterByStatuses: [],
        isLoading: false,
        isReadOnly: false,
        moreActionsMenuOption: [{ name: 'Manage Passes', icon: 'pass' }],
        numberOfPassesApproved: 0,
        numberOfPassesPending: 0,
        numberOfPassesRejected: 0,
        numberOfPassesRequested: 0,
        numberOfPassesReserved: 0,
        searchByUserName: '',
        searchByOrganisation: '',
        searchByPosition: '',
        searchByMediaType: '',
        searchByHolderType: '',
        selectedItem: {},
        showNotAppliedTable: false,
        tableHeaders: [
            { text: 'ID', value: 'id', align: 'left', sortable: false, hidden: true },
            { text: 'Status', value: 'accreditationPassStatus', align: 'center', sortable: false, width: '96px' },
            { text: 'Name', value: 'userName', align: 'start', sortable: false },
            { text: 'Organisation', value: 'userOrganisation', align: 'start', sortable: false },
            { text: 'Position', value: 'userPosition', align: 'start', sortable: false },
            { text: 'Press Type', value: 'userPressType', align: 'start', sortable: false },
            { text: 'Holder Type', value: 'userHolderType', align: 'start', sortable: false },
            { text: '', value: 'action', align: 'right', sortable: false, width: '48px' }
        ],

        // Pagination
        currentPage: 1,
        itemsPerPage: 10
    }),

    computed: {
        /**
         * Users Who Have Applied
         * Returns users who have a pass status of Approved, Pending, Rejected, or Reserved
         */
        usersWhoHaveApplied() {
            return this.computedTableData.filter((item) =>
                ['Approved', 'Pending', 'Rejected', 'Reserved'].includes(item.passData?.accreditationPassStatus)
            )
        },

        /**
         * Users Who Have Not Applied
         * Returns users who have no pass or status of 'Not Applied'
         */
        usersWhoHaveNotApplied() {
            return this.computedTableData.filter(
                (item) => !item.passData?.accreditationPassStatus || item.passData.accreditationPassStatus === 'Not Applied'
            )
        },
        computedHeadersForNotApplied() {
            return this.tableHeaders.filter(
                (header) => !header.hidden && header.value !== 'accreditationPassStatus' && header.value !== 'userHolderType' // Remove holder type for users who haven't applied
            )
        },
        /**
         * Is Organisation User
         *
         * Check if the current user is an Organisation-User level
         *
         * @returns {boolean}
         */
        isOrganisationUser() {
            return this.MIX_getCurrentUser().userLevel === 'Organisation-User'
        },
        /**
         * Computed Headers
         *
         * Remove hidden headers and only show the actions column to those with access.
         *
         * @returns an array of header objects
         */
        computedHeaders() {
            const t = this
            let headers = t.tableHeaders

            // Remove hidden headers
            headers = headers.filter((h) => !h.hidden)

            return headers
        },

        /**
         * Computed Number of Active Filters
         *
         * Count the number of active filters to display in the filters button badge.
         *
         * @returns {*}
         */
        computedNumberOfActiveFilters() {
            const t = this
            return [
                t.filterByHolderTypes.length,
                t.filterByMediaTypes.length,
                t.filterByPosition ? 1 : 0,
                t.filterByPrintStatus.length,
                t.filterByStatuses.length
            ].reduce((acc, curr) => acc + curr, 0)
        },

        /**
         * Computed Paginated Data
         *
         * Return the paginated data for the mobile view.
         * The data is sliced based on the current page and items per page.
         *
         * Note: This is only used for the mobile view, and is required so the mobile view does crash when the data is too large.
         *
         * @returns {array[{}]} an array of objects
         */
        computedPaginatedData() {
            const t = this

            const START_PAGE = (t.currentPage - 1) * t.itemsPerPage
            const END_PAGE = START_PAGE + t.itemsPerPage

            return t.computedTableData.slice(START_PAGE, END_PAGE)
        },

        /**
         * Computed Table Data
         *
         * Process and filter table data based on:
         * 1. User role and organisation access
         * 2. Search criteria
         * 3. Status filters
         * 4. Other filters (print status, holder type, position)
         *
         * @returns {Array} Filtered and sorted table data
         */
        computedTableData() {
            const t = this
            const currentUser = t.MIX_getCurrentUser()
            let tableData = t.$props.fullEventData?.passesData || []

            // 1. Filter based on user role and organisation access
            if (currentUser.userLevel === 'Staff-Admin') {
                // Staff-Admin sees all data
                tableData = [...tableData]
            } else if (currentUser.userLevel === 'Organisation-User') {
                // Regular users only see themselves, but with all their orgs
                tableData = tableData.filter((item) => item.userData.entityId === currentUser.entityId)
                // Remove duplicates by entityId (keep first occurrence)
                tableData = Object.values(
                    tableData.reduce((acc, item) => {
                        if (!acc[item.userData.entityId]) {
                            acc[item.userData.entityId] = item
                        }
                        return acc
                    }, {})
                )
            } else if (currentUser.userLevel === 'Organisation-Manager') {
                // Get current user's organisations
                const userOrgs = Array.isArray(currentUser.userOrganisation) ? currentUser.userOrganisation : [currentUser.userOrganisation]

                // Show users from any of the manager's organisations
                tableData = tableData.filter((item) => {
                    const itemOrgs = Array.isArray(item.userData.userOrganisation) ? item.userData.userOrganisation : [item.userData.userOrganisation]
                    return itemOrgs.some((org) => userOrgs.includes(org))
                })
                // Remove duplicates
                tableData = Object.values(
                    tableData.reduce((acc, item) => {
                        if (!acc[item.userData.entityId]) {
                            acc[item.userData.entityId] = item
                        }
                        return acc
                    }, {})
                )
            }

            // 2. Apply search filter
            if (t.searchByUserName) {
                const searchTerm = t.searchByUserName.toUpperCase()
                tableData = tableData.filter((item) => item.userData.userName.toUpperCase().includes(searchTerm))
            }

            // 3. Apply status filters
            if (t.filterByStatuses.length) {
                tableData = tableData.filter((item) => t.filterByStatuses.includes(item.passData?.accreditationPassStatus))
            }

            // 4. Apply other filters
            if (t.filterByPrintStatus.length) {
                tableData = tableData.filter((item) =>
                    t.filterByPrintStatus.includes(item.passData?.accreditationPassPrinted ? 'Printed' : 'Not Printed')
                )
            }

            if (t.filterByHolderTypes.length) {
                tableData = tableData.filter((item) => t.filterByHolderTypes.includes(item.passData?.accreditationPassHolderType))
            }

            if (t.filterByPosition) {
                const positionTerm = t.filterByPosition.toUpperCase()
                tableData = tableData.filter((item) => (item.userData?.userPosition?.toUpperCase() || '').includes(positionTerm))
            }

            // 5. Sort by username
            return tableData.sort((a, b) => (a.userData?.userName || '').localeCompare(b.userData?.userName || ''))
        },

        /**
         * Computed Pass Counts
         *
         * Calculate all pass counts
         *
         * @returns {Object}
         */
        computedPassCounts() {
            const counts = {
                requested: 0,
                pending: 0,
                approved: 0,
                rejected: 0,
                reserved: 0
            }

            this.computedTableData.forEach((item) => {
                const status = item.passData?.accreditationPassStatus
                if (['Pending', 'Approved', 'Rejected', 'Reserved'].includes(status)) {
                    counts.requested++
                }
                switch (status) {
                    case 'Pending':
                        counts.pending++
                        break
                    case 'Approved':
                        counts.approved++
                        break
                    case 'Rejected':
                        counts.rejected++
                        break
                    case 'Reserved':
                        counts.reserved++
                        break
                }
            })

            return counts
        },

        /**
         * Computed Current Page
         *
         * Return the current page number.
         *
         * @returns {number} - the current page number
         */
        computedTotalPages() {
            return Math.ceil(this.computedTableData.length / this.itemsPerPage)
        }
    },

    watch: {
        /**
         * Watch computedPassCounts
         *
         * Update the data properties when counts change
         */
        computedPassCounts: {
            handler(newCounts) {
                this.numberOfPassesRequested = newCounts.requested
                this.numberOfPassesPending = newCounts.pending
                this.numberOfPassesApproved = newCounts.approved
                this.numberOfPassesRejected = newCounts.rejected
                this.numberOfPassesReserved = newCounts.reserved
            },
            immediate: true
        }
    },

    methods: {
        /**
         * Get Organisation Name
         *
         * Get the organisation name from the organisation ID using the parent component's merged data
         *
         * @param {String} orgId - The organisation ID
         * @returns {String} - The organisation name or ID if not found
         */
        getOrganisationName(orgId) {
            const t = this

            // First try to get from the organizations map
            const organisationName = t.$props.fullEventData?.organisations?.[orgId]
            if (organisationName) {
                return organisationName
            }

            // If not found in map, try to find in passesData
            const foundOrg = t.$props.fullEventData?.passesData?.find((pass) => pass.organisationData?.entityId === orgId)?.organisationData
                ?.organisationName

            return foundOrg || orgId
        },
        /**
         * Get MIX Current User
         *
         * Get the current user's data in the correct format for pass application
         *
         * @returns {Object} User data formatted for pass application
         */
        getMIXCurrentUser() {
            const currentUser = this.MIX_getCurrentUser()
            return {
                userData: currentUser,
                passData: {
                    accreditationPassStatus: 'Pending'
                }
            }
        },
        /**
         * Clear Filters
         *
         * Clear all the filters.
         */
        clearFilters() {
            const t = this

            t.filterByHolderTypes = []
            t.filterByMediaTypes = []
            t.filterByPosition = ''
            t.filterByPrintStatus = []
            t.filterByStatuses = []
        },

        /**
         * Handle Emitted Menu Selection
         *
         * Take the emitted selection from the menu, and call the relevant function.
         *
         * @param selection a string-based menu option
         */
        handleEmittedMenuSelection(selection) {
            const t = this

            // if (selection === 'Event Settings') t.$emit('openEventSettings', t.$props.fullEventData)
            if (selection === 'Manage Passes') t.handleUserSelection({})
        },

        /**
         * Handle Filter Status Selection
         *
         * Add or remove the selected status from the filterStatuses array.
         *
         * @param status {string} the status to add or remove
         */
        handleFilterStatusSelection(status) {
            const t = this

            if (t.filterByStatuses.includes(status)) t.filterByStatuses = t.filterByStatuses.filter((s) => s !== status)
            else t.filterByStatuses.push(status)
        },

        handleUserSelection(item) {
            const t = this
            const CURRENT_USER = t.MIX_getCurrentUser()

            // Only Organisation-Users are restricted to selecting themselves
            if (CURRENT_USER.userLevel === 'Organisation-User' && item.userData?.entityId !== CURRENT_USER.entityId) {
                return
            }

            // Check if this is from "Apply for Passes" button by comparing the current user's data
            const isApplyForPassesClick = item.userData?.entityId === CURRENT_USER.entityId && item.passData?.accreditationPassStatus === 'Pending'

            if (isApplyForPassesClick) {
                t.$emit('selectedUser', {
                    ...item,
                    isFromApplyButton: true
                })
            } else {
                // Normal user selection from list
                t.$emit('selectedUser', {
                    ...item,
                    isSpecificallySelected: true
                })
            }
        },

        /**
         * Next Page
         *
         * Go to the next page.
         */
        nextPage() {
            const t = this

            if (t.currentPage < t.computedTotalPages) t.currentPage += 1
        },

        /**
         * Prev Page
         *
         * Go to the previous page.
         */
        prevPage() {
            const t = this

            if (t.currentPage > 1) t.currentPage -= 1
        },

        /**
         * Toggle Filters Visibility
         *
         * Toggle the visibility of the filters panel.
         */
        toggleFiltersVisibility() {
            const t = this

            t.isFiltersPanelVisible = !t.isFiltersPanelVisible
        },

        /**
         * Handle Back
         *
         * Navigate back to organisations view
         */
        handleBack() {
            this.$emit('backToEvents')
        }
    }
}
</script>

<style scoped></style>
